import React, { useEffect, useRef, useState } from "react";
import "./CallReviewTable.css";
import { FaEdit } from "react-icons/fa";
import CallReviewSheetPopup, {
  formatReviewDateTime,
} from "../Popups/CallReviewSheetPopup";
import {
  postCallReview,
  previewCallReviewEmail,
} from "../../../operations/services/postCallReview";
import { notify } from "../../../common/Helper/notification";
import { findAgentEmail } from "../../Helper/findAgentEmail";
import { useDispatch, useSelector } from "react-redux";
import { setMNScore } from "../../../redux/slices/operationHomeSlice";
import { deriveCallReviewSections } from "./callReviewScoring";

const VONAGE_LOOKUP_INSTRUCTION =
  "(Copy/paste this ID in the top search bar in Salesforce to find this call's recording/transcript)";

/* ─── Score color coding (Insights Details simplification) ───
 * Performance-band colors for % scores so reviewers can triage at a glance:
 *   >= 75%  green,  50–74%  amber,  < 50%  red.
 * Colors are chosen for sufficient contrast in both the app and the
 * screenshot-based PDF export. */
const scoreColorStyle = (pct) => {
  const n = Number(pct);
  if (!Number.isFinite(n)) return undefined;
  const color = n >= 75 ? "#1a7f37" : n >= 50 ? "#b26a00" : "#c62828";
  return { color, fontWeight: 600 };
};

/* One section row of the summary table. When every criterion in a section is
 * marked N/A its weighted max is 0 — there's nothing to score — so we render
 * "—" for Achieved Score / Score out of 5 / % Score and skip the red/amber/green
 * performance band (a 0% "fail" color there is misleading, since the section
 * simply wasn't scored). Sections with any scored criterion are unchanged. */
const SectionSummaryRow = ({ label, section }) => {
  const max = section?.max ?? 0;
  const allNA = max === 0;
  const pct = section?.percentage ?? 0;
  const colorStyle = allNA ? undefined : scoreColorStyle(pct);
  return (
    <tr>
      <td>{label}</td>
      <td>{max}</td>
      <td style={colorStyle}>
        {allNA ? "—" : (section?.achieved ?? 0).toFixed(2)}
      </td>
      <td>{allNA ? "—" : (section?.outOf5 ?? 0).toFixed(2)}</td>
      <td style={colorStyle}>{allNA ? "—" : `${pct.toFixed(1)}%`}</td>
    </tr>
  );
};

/* ─── Call Review Sheet — criteria breakdown ───
 * The verbose per-criterion score tables (Score / Weightage / Max / Achieved)
 * largely duplicated the summary scores above. Per the reviewer, the genuinely
 * NEW information is *which* criteria were met, so each section is pivoted into
 * one compact row: the criteria scored Yes go under "Yes", the rest under "No".
 * The full per-criterion scoring is still available via "Edit Scores" and the
 * PDF export. `SHORT_LABELS` are display-only short names (indexed by position,
 * matching the raw field order in callReviewScoring); the underlying scoring is
 * unchanged. A criterion counts as "Yes" when its score is >= 1. */
const SHORT_LABELS = {
  "Using the Tools": [
    "Intro Script",
    "4Ws Discovery Question 1",
    "4Ws Discovery Question 2",
    "4Ws Discovery Question 3",
    "4Ws Discovery Question 4",
    "Call Control",
    "Referral Source",
  ],
  Sales: [
    "Understanding Wants and Needs",
    "Individualized Program Pitch",
    "Empathy to Caller's Situation",
  ],
  Objections: [
    "Assessment Process Initiated",
    "Insurance Without Creating a Barrier",
    "Overcoming Objections",
    "Creating a Sense of Urgency",
    "Scheduling Next Steps",
  ],
};

const listCell = (items) =>
  items.length ? (
    items.map((x, i) => (
      <div key={i} className="crit-item">
        {x}
      </div>
    ))
  ) : (
    <span className="crit-empty">—</span>
  );

const CriteriaBreakdown = ({ sections }) => {
  const rows = sections.map((sec) => {
    const labels = SHORT_LABELS[sec.title] || sec.fields;
    const yes = [];
    const no = [];
    sec.fields.forEach((field, i) => {
      const label = labels[i] || field;
      const v = sec.values[i];
      // "NA" criteria are excluded from scoring, so they aren't shown as either
      // Yes or No (rather than being miscounted as a "No").
      if (v === "NA") return;
      (Number(v ?? 0) >= 1 ? yes : no).push(label);
    });
    return { title: sec.title, yes, no };
  });

  return (
    <div className="sub-section">
      <h4 className="sub-title">Call Review Sheet</h4>
      <div className="table-wrapper">
        <table className="call-review-table criteria-3col">
          <thead>
            <tr>
              <th>Category</th>
              <th>Yes</th>
              <th>No</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="crit-cat">{r.title}</td>
                <td className="crit-yes">{listCell(r.yes)}</td>
                <td className="crit-no">{listCell(r.no)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─── */

const CallReviewTable = ({
  callReviewData,
  callId,
  onRefetch,
  agentName,
  memberName,
  reviewDate,
  vonageCallId,
  magicHealthId,
  // Incrementing signal from the Details page's top-right button — opens this
  // scoring modal (same as the in-table "Edit Scores" action).
  openSignal,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const dispatch = useDispatch();

  // Open the scoring popup when the external signal changes (ignore the initial
  // value so it doesn't auto-open on mount).
  const lastSignalRef = useRef(openSignal);
  useEffect(() => {
    if (openSignal !== undefined && openSignal !== lastSignalRef.current) {
      lastSignalRef.current = openSignal;
      setIsOpen(true);
    }
  }, [openSignal]);

  const loginData = useSelector((state) => state.login.login.data);
  const userRole = loginData?.data[0]?.Role;

  // Reviewer name for the call-review payload — combines whichever of
  // FirstName/LastName is present so a partial profile still yields a name
  // instead of an empty string.
  const reviewerUser = loginData?.data?.[0];
  const reviewerName = [reviewerUser?.FirstName, reviewerUser?.LastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  // Computed safely with optional chaining so the useEffect below can run
  // unconditionally (hooks must not be called after an early return).
  const mnScore = parseFloat((callReviewData?.average?.outOf5 ?? 0).toFixed(2));

  useEffect(() => {
    if (!callId || !callReviewData) return;
    dispatch(setMNScore({ callId, score: mnScore }));
  }, [callId, callReviewData, dispatch, mnScore]);

  if (!callReviewData) return null;

  const { emailRecipients = [] } = callReviewData;

  // Audit trail for the header. `updatedBy` stores the reviewer's email;
  // `updatedByName` is the display name the backend resolved from AdminDetails,
  // which is what belongs on screen. Older rows hold "System" or a bare name
  // rather than an email and resolve to themselves, so they still name someone.
  const auditName = (callReviewData.updatedByName || "").trim();
  const auditAt = formatReviewDateTime(callReviewData.updatedAt);

  const derived = deriveCallReviewSections(callReviewData);
  const { utSection, sSection, oSection, totals, average, totalScore5 } =
    derived.summary;
  // Every criterion marked N/A → the whole sheet has nothing to score (weighted
  // max is 0). Show "—" (not 0 / a red 0%) for the Total and Average rows too,
  // matching the per-section rows above.
  const allNA = (totals?.max ?? 0) === 0;
  const usingTools = derived.usingTools;
  const sales = derived.sales;
  const objections = derived.objections;

  const agentEmail = findAgentEmail(agentName, emailRecipients)?.email || "";

  console.log("[CallReviewTable] agentName:", agentName);
  console.log("[CallReviewTable] emailRecipients:", emailRecipients);
  console.log("[CallReviewTable] matched agentEmail:", agentEmail);

  const handleEditClick = () => setIsOpen(true);

  const mapYesNo = (value) =>
    value === "NA" ? "NA" : value === 1 ? "Yes" : "No";

  const handleSaveCallReview = async (data, options = {}) => {
    try {
      if (!callId) return notify.info("Call ID is missing");
      if (!data) return notify.info("No data to save");

      setSaving(true);

      const payload = {
        callid: callId,
        UT1: mapYesNo(data.usingTools?.[0]),
        UT2: {
          1: mapYesNo(data.usingTools?.[1]),
          2: mapYesNo(data.usingTools?.[2]),
          3: mapYesNo(data.usingTools?.[3]),
          4: mapYesNo(data.usingTools?.[4]),
        },
        UT3: mapYesNo(data.usingTools?.[5]),
        UT4:
          data.usingTools?.[6] === "NA"
            ? "NA"
            : mapYesNo((data.usingTools?.[6] ?? 0) >= 1 ? 1 : 0),
        S1: mapYesNo(data.sales?.[0]),
        S2: mapYesNo(data.sales?.[1]),
        S3: mapYesNo(data.sales?.[2]),
        O5: mapYesNo(data.objections?.[0]),
        O1: mapYesNo(data.objections?.[1]),
        O2: mapYesNo(data.objections?.[2]),
        O3: mapYesNo(data.objections?.[3]),
        O4: mapYesNo(data.objections?.[4]),
        WhatWentWell: data.whatWentWell || "",
        WhatCanImprove: data.whatCanImprove || "",
        GeneralComments: data.generalComments || "",
      };

      payload.sendEmail = !!options.sendEmail;
      if (options.sendEmail && options.agentEmail) {
        payload.agentEmail = options.agentEmail;
      }

      payload.reviewerName = reviewerName;
      // Only a real edit updates "Updated By" / "Last Updated" server-side.
      payload.contentChanged = options.contentChanged !== false;
      payload.convoUrl =
        typeof window !== "undefined" ? window.location.href : "";
      payload.senderEmail = reviewerUser?.Email || "";
      payload.vonageLookupInstruction = VONAGE_LOOKUP_INSTRUCTION;

      // Preview: same payload, rendered rather than sent or saved. Returns
      // straight to the caller so the modal can show it — no toast, and the
      // sheet stays open.
      if (options.previewOnly) {
        return await previewCallReviewEmail(payload);
      }

      // The body as the reviewer edited it in the preview. Forwarded only when
      // they actually changed something — null means untouched, and the backend
      // then renders the message fresh instead of echoing a stale copy.
      //
      // Set AFTER the preview returns above, so a preview never carries an
      // override into the render it is supposed to be previewing.
      if (options.emailHtmlOverride != null) {
        payload.emailHtmlOverride = options.emailHtmlOverride;
      }

      console.log("[CallReviewTable] postCallReview payload:", payload);

      const response = await postCallReview(payload);
      console.log("[CallReviewTable] postCallReview response:", response);

      if (options.sendEmail) {
        if (response?.emailSent) {
          notify.success("Feedback emailed to the agent successfully");
        } else {
          notify.error(
            response?.emailError || "Failed to email feedback to the agent",
          );
        }
      } else {
        notify.success("Call review saved successfully");
      }

      setIsOpen(false);
      onRefetch?.();
      return response;
    } catch (error) {
      notify.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="call-review-container">
      <div className="header-row">
        <div className="edit-icon-wrapper" onClick={() => setIsOpen(true)}>
          <FaEdit className="edit-icon" />
          <span className="edit-text">Edit Scores</span>
        </div>

        {/* Who last reviewed this call, and when — on the page itself, not only
            inside the modal. Someone scanning the call details can see whether
            it has been reviewed and by whom without opening anything.

            Each half is rendered only when it has a value: a call nobody has
            reviewed shows neither, rather than a label with a blank after it
            that reads as data failing to load. Both come from the same GET the
            modal reads, so the page and the modal cannot disagree. */}
        {(auditName || auditAt) && (
          <div className="call-review-audit">
            {auditName && (
              <span className="call-review-audit-item">
                <strong>Updated By:</strong> {auditName}
              </span>
            )}
            {auditAt && (
              <span className="call-review-audit-item">
                <strong>Last Updated:</strong> {auditAt}
              </span>
            )}
          </div>
        )}
      </div>

      {/* SUMMARY */}
      <div className="table-wrapper">
        <table className="call-review-table summary-5col">
          <thead>
            <tr>
              <th>Scoring Category</th>
              <th>Max Score</th>
              <th>Achieved Score</th>
              {/* <th>Final</th> */}
              <th>Score out of 5</th>
              <th>% Score</th>
            </tr>
          </thead>

          <tbody>
            <SectionSummaryRow label="Using the Tools" section={utSection} />
            <SectionSummaryRow label="Sales" section={sSection} />
            <SectionSummaryRow label="Objections" section={oSection} />

            <tr className="total-row">
              <td>Total</td>
              <td>{totals.max ?? 0}</td>
              <td>{allNA ? "—" : (totals.achieved ?? 0).toFixed(2)}</td>
              {/* <td></td> */}
              <td>{allNA ? "—" : totalScore5.toFixed(2)}</td>
              <td></td>
            </tr>

            <tr className="average-row">
              <td>Average Score</td>
              <td></td>
              <td></td>
              {/* <td>{(average.final ?? 0).toFixed(2)}</td> */}
              <td>{allNA ? "—" : (average.outOf5 ?? 0).toFixed(2)}</td>
              <td
                style={
                  allNA ? undefined : scoreColorStyle(average.percentage ?? 0)
                }
              >
                {allNA ? "—" : `${(average.percentage ?? 0).toFixed(2)}%`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <CriteriaBreakdown sections={[usingTools, sales, objections]} />

      {/* POPUP */}
      <CallReviewSheetPopup
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSaveCallReview}
        isSaving={saving}
        initialData={{
          usingTools: usingTools.values,
          sales: sales.values,
          objections: objections.values,
          whatWentWell: callReviewData?.feedback?.whatWentWell || "",
          whatCanImprove: callReviewData?.feedback?.whatCanImprove || "",
          generalComments: callReviewData?.feedback?.generalComments || "",
        }}
        reviewData={callReviewData}
        callId={callId}
        agentName={agentName}
        agentEmail={agentEmail}
        memberName={memberName}
        reviewDate={reviewDate}
        vonageCallId={vonageCallId}
        magicHealthId={magicHealthId}
      />
    </div>
  );
};

export default CallReviewTable;
